import { useDebounce } from '@uidotdev/usehooks';
import { buildTextSearchClauses } from 'utils/buildTextSearchClauses';
import {
  SearchSearchCandidateProfileQueriesQuery,
  useSearchSearchCandidateProfileQueriesQuery
} from 'generated-graphql/hooks/apollo-hooks';
import {
  SearchableSortDirection,
  SearchableSearchCandidateProfileQuerySortableFields,
  SearchableSearchCandidateProfileQuerySortInput
} from 'generated-graphql/types';
import { useUser } from 'core/stores/hooks/useUser';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useState, useMemo, useCallback } from 'react';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';

const SEARCH_DEBOUNCE_MS = 750;

const SEARCH_SORT: SearchableSearchCandidateProfileQuerySortInput[] = [
  {
    field: SearchableSearchCandidateProfileQuerySortableFields.CreatedAt,
    direction: SearchableSortDirection.Desc
  }
];

type QueryData = SearchSearchCandidateProfileQueriesQuery;
type QueryItems = NonNullable<QueryData['searchSearchCandidateProfileQueries']>;
type QueryItem = NonNullable<QueryItems['items'][number]>;
type UpdateQueryFn = (mapFn: (prev: QueryData) => QueryData) => void;

const getQueryItems = (data: QueryData) =>
  data.searchSearchCandidateProfileQueries;

const updateQueryItems = (
  prev: QueryData,
  update: (items: QueryItems) => Partial<QueryItems>
): QueryData => {
  const current = getQueryItems(prev);
  if (!current) return prev;

  return {
    ...prev,
    searchSearchCandidateProfileQueries: { ...current, ...update(current) }
  };
};

export const useSearchCandidateProfileQueryHistoryList = () => {
  const user = useUser();
  const organization = useOrganization();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  const baseFilter = useMemo(
    () => ({
      userId: { eq: user.id },
      organizationId: { eq: organization.id },
      isArchived: { ne: true },
      ...(debouncedSearchTerm && {
        or: buildTextSearchClauses('displayTitle', debouncedSearchTerm)
      })
    }),
    [user.id, organization.id, debouncedSearchTerm]
  );

  const {
    data: pinnedData,
    loading: pinnedLoading,
    updateQuery: updatePinnedQuery
  } = useSearchSearchCandidateProfileQueriesQuery({
    variables: {
      limit: 50,
      sort: SEARCH_SORT,
      filter: { ...baseFilter, isPinned: { eq: true } }
    },
    fetchPolicy: 'cache-and-network'
  });

  const {
    data: recentData,
    loading: recentLoading,
    updateQuery: updateRecentQuery
  } = useSearchSearchCandidateProfileQueriesQuery({
    variables: {
      limit: 50,
      sort: SEARCH_SORT,
      filter: { ...baseFilter, isPinned: { ne: true } }
    },
    fetchPolicy: 'cache-and-network'
  });

  const updateBothQueries = useCallback(
    (mapFn: (prev: QueryData) => QueryData) => {
      updatePinnedQuery(mapFn);
      updateRecentQuery(mapFn);
    },
    [updatePinnedQuery, updateRecentQuery]
  );

  const addItemToQuery = (query: UpdateQueryFn, item: QueryItem) => {
    query((prev) =>
      updateQueryItems(prev, ({ items, total }) => ({
        items: [item, ...items],
        total: (total ?? 0) + 1
      }))
    );
  };

  const updatePinStatus = useCallback(
    (id: string, isPinned: boolean) => {
      const sourceQuery = isPinned ? updateRecentQuery : updatePinnedQuery;
      const targetQuery = isPinned ? updatePinnedQuery : updateRecentQuery;

      sourceQuery((prev) =>
        updateQueryItems(prev, ({ items, total }) => {
          const item = items.find((i) => i?.id === id);
          if (!item) return {};

          addItemToQuery(targetQuery, { ...item, isPinned });

          return {
            items: items.filter((i) => i?.id !== id),
            total: (total ?? 0) - 1
          };
        })
      );
    },
    [updatePinnedQuery, updateRecentQuery]
  );

  const updateDisplayTitle = useCallback(
    (id: string, displayTitle: string) => {
      updateBothQueries((prev) =>
        updateQueryItems(prev, ({ items }) => ({
          items: items.map((item) =>
            item?.id === id ? { ...item, displayTitle } : item
          )
        }))
      );
    },
    [updateBothQueries]
  );

  const removeItem = useCallback(
    (id: string) => {
      updateBothQueries((prev) =>
        updateQueryItems(prev, ({ items, total }) => {
          if (!items.some((i) => i?.id === id)) return {};
          return {
            items: items.filter((i) => i?.id !== id),
            total: (total ?? 0) - 1
          };
        })
      );
    },
    [updateBothQueries]
  );

  return {
    pinnedList: extractQueryListItems(pinnedData),
    recentList: extractQueryListItems(recentData),
    loading: pinnedLoading || recentLoading,
    searchTerm,
    setSearchTerm,
    updatePinStatus,
    updateDisplayTitle,
    removeItem
  };
};
